import { useMemo } from 'react';
import { ErrorFeed, Header, ServiceList, StatusBanner } from '@/components';
import { useRecentErrors, useServiceStatus } from '@/hooks';
import { calculateStatusFromAggregates } from '@/services/statusCalculator';
import { readDemoScenario } from '@/dev/fixtures';
import { HealthFeedState } from '@/types';

export default function App() {
  // Two independent sources, deliberately. Service health comes from the PUBLIC
  // aggregate so a signed-out visitor sees real status; the error feed stays on
  // `errors`, which only admins may read. A locked feed no longer implies
  // unknown health.
  const { aggregates, loading: statusLoading, error: statusError } = useServiceStatus();
  const { errors, loading, error: feedError } = useRecentErrors(100);

  // Dev-only: substitutes the INPUT to the calculator so degraded/outage states
  // can be inspected before `serviceStatus` is deployed. Dead code in a build.
  const demo = useMemo(() => readDemoScenario(), []);

  // Three states, not two. `loading` used to be discarded, which meant the
  // first paint of every visit ran the calculator over an empty array and
  // rendered a confident "All Systems Operational" before a single byte of
  // health data had arrived. An unanswered question is not a good answer.
  const feedState: HealthFeedState =
    demo?.feedState ?? (statusError ? 'unavailable' : statusLoading ? 'connecting' : 'live');
  const healthError = demo ? demo.feedError ?? null : statusError;

  const statusSummary = useMemo(
    () => calculateStatusFromAggregates(demo ? demo.aggregates : aggregates),
    [aggregates, demo]
  );

  // Anything other than a live read means we cannot speak to service health.
  const healthUnknown = feedState !== 'live';

  // A clean read of an EMPTY collection is not the same evidence as a clean
  // read of ten healthy services, even though the calculator scores them
  // identically. Say which one the reader is looking at.
  const noReports = feedState === 'live' && (demo ? demo.aggregates : aggregates).length === 0;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Header status={statusSummary.overall} feedState={feedState} />

      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="space-y-5">
          <StatusBanner
            summary={statusSummary}
            feedState={feedState}
            feedError={healthError}
            noReports={noReports}
          />

          <ServiceList
            services={statusSummary.services}
            dataUnavailable={healthUnknown}
          />

          <ErrorFeed
            errors={(demo?.errors ?? errors).slice(0, 20)}
            loading={demo ? false : loading}
            loadError={demo ? (demo.errors ? null : feedError) : feedError}
            title="Recent Errors (24h)"
          />
        </div>

        <footer className="mt-10 border-t border-slate-200 pt-6 text-xs leading-relaxed text-slate-500">
          {/* Saying where each half comes from is not pedantry: this page has
              twice been read as authoritative when half of it was locked. */}
          <p>
            Service health is derived from failures Someta's services report
            about themselves, and is public. Individual error reports are
            restricted to Someta admins — signing in adds detail, it does not
            change the verdict above.
          </p>
          <p className="mt-3">
            <a
              href="https://someta.app"
              className="font-medium text-indigo-600 transition-colors hover:text-indigo-800"
            >
              Go to Someta →
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}
